"use client";

import { useState } from "react";
import { z } from "zod";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";


import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type NewLeadFormProps = {
  users: any[];
  accounts: any[];
};


const formSchema = z.object({
  first_name: z.string(),
  middle_name: z.string().optional(),
  last_name: z.string().min(3).max(30).nonempty(),
  company: z.string().optional(),
  email: z.string().email().optional(),
  phone: z.string().min(0).max(15).optional(),
  ssn: z.string().regex(/^\d{3}-\d{2}-\d{4}$/, "Invalid SSN format, should be '123-45-6789'"),
  advisorName: z.string().optional().nullable(),
  advisorEmail: z.string().email().optional().nullable(),
  isReturningInvestor: z.boolean().optional(),
  ownsEquipmentCompany: z.boolean().optional(),
  investmentAmount: z.number().optional(),
  percentageOwnership: z.number().optional(),
  city: z.string().optional().nullable(),
  postal_code: z.string().optional().nullable(),
  state: z.string().optional().nullable(),
  street_address: z.string().optional().nullable(),
  businessType: z.string().optional().nullish(),
  business_owner: z.enum(['yes', 'no']).optional(),




  equipmentLLCName: z.string().optional(),
equipmentLLCEIN: z.string().optional(),
equipmentLLCAddress: z.string().optional(),
downPaymentAmount: z.number().optional(),
});






type NewLeadFormValues = z.infer<typeof formSchema>;

export function NewLeadForm({ users, accounts }: NewLeadFormProps) {
  const router = useRouter();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const form = useForm<NewLeadFormValues>({
    resolver: zodResolver(formSchema),
  });

  const isBusinessOwner = form.watch("business_owner") === "yes";

  const onSubmit = async (data: NewLeadFormValues) => {
    setIsLoading(true);
    try {
      await axios.post("/api/crm/leads", data);
      toast({
        title: "Success",
        description: "Lead created successfully",
      });
      form.reset();
      router.refresh();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.response?.data,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-4xl mx-auto space-y-8">
        {/* Personal Information Section */}
        <section>
          <h2 className="text-2xl font-semibold mb-4">Personal Information</h2>
          <div className="grid grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First name</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} placeholder="John" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="middle_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Middle name</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} placeholder="Doe" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last name</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} placeholder="Doe" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} placeholder="johny@domain.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} placeholder="+1234567890" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
           <FormField
  control={form.control}
  name="ssn"
  rules={{
    required: 'SSN is required',
    pattern: {
      value: /^\d{3}-\d{2}-\d{4}$/, // Allows "123-45-6789" format
      message: 'Invalid SSN format, should be "123-45-6789"',
    },
  }}
  render={({ field, fieldState }) => (
    <FormItem>
      <FormLabel>SSN</FormLabel>
      <FormControl>
        <Input 
          {...field} 
          placeholder="123-45-6789" 
        />
      </FormControl>
      {fieldState.error && (
        <p style={{ color: 'red' }}>{fieldState.error.message}</p>
      )}
    </FormItem>
  )}
/>
          </div>
        </section>

        {/* Address Section */}
        <section>
          <h2 className="text-2xl font-semibold mb-4">Address</h2>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="street_address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street Address</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} placeholder="Street Address" value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} placeholder="City" value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} placeholder="State" value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="postal_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Postal Code</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} placeholder="Postal Code" value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </section>

        {/* Advisor Section */}
        <section>
          <h2 className="text-2xl font-semibold mb-4">Advisor</h2>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="advisorName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Advisor Name</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} placeholder="Advisor Name" value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="advisorEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Advisor Email</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} type="email" placeholder="advisor@example.com" value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </section>

        {/* Investment Section */}
        <section>
          <h2 className="text-2xl font-semibold mb-4">Investment</h2>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="isReturningInvestor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Is Returning Investor</FormLabel>
                  <FormControl>
                    <input
                      type="checkbox"
                      disabled={isLoading}
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="ownsEquipmentCompany"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Owns Equipment Company</FormLabel>
                  <FormControl>
                    <input
                      type="checkbox"
                      disabled={isLoading}
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="investmentAmount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Investment Amount</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      disabled={isLoading}
                      {...field}
                      placeholder="0"
                      onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="percentageOwnership"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Percentage Ownership</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      disabled={isLoading}
                      {...field}
                      placeholder="0"
                      onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </section>

        {/* Business Section */}
        <section>
          <h2 className="text-2xl font-semibold mb-4">Business</h2>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="business_owner"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Owner</FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select business owner status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isBusinessOwner && (
              <FormField
                control={form.control}
                name="businessType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Type</FormLabel>
                    <FormControl>
                      <Select onValueChange={field.onChange} defaultValue={field.value ?? undefined}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select business type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LLC">LLC</SelectItem>
                          <SelectItem value="Corporation">Corporation</SelectItem>
                          <SelectItem value="Partnership">Partnership</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        </section>

       
    {/* Equipment LLC Section */}
    <section>
      <h2 className="text-2xl font-semibold mb-4">Equipment LLC</h2>
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="equipmentLLCName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Equipment LLC Name</FormLabel>
              <FormControl>
                <Input disabled={isLoading} {...field} placeholder="Equipment LLC Name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="equipmentLLCEIN"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Equipment LLC EIN</FormLabel>
              <FormControl>
                <Input disabled={isLoading} {...field} placeholder="Equipment LLC EIN" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="equipmentLLCAddress"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Equipment LLC Address</FormLabel>
              <FormControl>
                <Input disabled={isLoading} {...field} placeholder="Equipment LLC Address" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="downPaymentAmount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Down Payment Amount</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  disabled={isLoading}
                  {...field}
                  placeholder="0"
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </section>




        {/* Submit Button */}
        <div className="flex justify-end">
          <Button disabled={isLoading} type="submit">
            {isLoading ? (
              <span className="flex items-center animate-pulse">Saving data...</span>
            ) : (
              "Create Lead"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}